import { showDialog } from '@/utils/dialogs';
/**
 * Library Screen - Document Management
 *
 * Simple, clean design consistent with other app screens.
 * Manages voice transcripts and text documents.
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  View,
  TouchableOpacity,
  ActivityIndicator,
  TextInput,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { useRouter, useLocalSearchParams } from 'expo-router';
import * as DocumentPicker from 'expo-document-picker';

import { Text } from '@/components/ui/text';
import { WebAppShell } from '@/design-system/components';
import {
  userDocumentsAPI,
  transcriptionAPI,
  documentsAPI,
  taskAPI,
  documentSharingAPI,
  UserDocumentItem,
} from '@/services/api';
import { TranscriptViewer } from '@/components/voice/TranscriptViewer';
import {
  TranscriptionModal,
  TranscriptionProgress,
  AnalysisProgress,
} from '@/components/voice';
import { AnalysisModal } from '@/components/analysis/AnalysisModal';
import { ShareDocumentModal } from '@/components/sharing/ShareDocumentModal';
import { ManageShareRecipientsModal } from '@/components/sharing/ManageShareRecipientsModal';
import { NotificationBell } from '@/components/notifications/NotificationBell';
import { NotificationListModal } from '@/components/notifications/NotificationListModal';
import { useTranslation } from '@/hooks/useTranslation';
import { useToast } from '@/context/ToastContext';
import { useResponsive } from '@/hooks/useResponsive';
import { useAudioUploadLimit } from '@/hooks/useAudioUploadLimit';
import { audioMaxBytes } from '@/config/uploadLimits';
import { userFacingErrorMessage } from '@/services/errorMessages';
import { trialAnalysisUsedMessage } from '@/utils/trialAnalysisUsed';
import { noCreditMessage } from '@/utils/creditErrors';
import { audioUploadRejectionMessage, transcriptionFailureEvent, voiceMinutesExceededMessage } from '@/utils/transcribeError';
import { trackAuthEvent } from '@/services/analytics';
import { analyticsErrorCode } from '@/services/analyticsPrivacy';
import { libraryPickEvent, type LibraryPickOutcome } from './libraryAnalytics';
import { AUDIO_UPLOAD_MIME_TYPES, isAudioUploadFile } from '@/utils/audioUploadFormats';
import { useTabAccess } from '@/hooks/useTabAccess';
import { useAuth } from '@/context/AuthContext';
import { canTrackIcfProgress } from '@/utils/billingAccess';
import {
  TranscriptContent,
  TranscriptListItem,
  TranscriptAnalysisItem,
  AnalysisOptions,
  TranscriptionOptions,
  SharedDocument,
} from '@/types';
import { useTaskCompletion } from '@/hooks/useTaskCompletion';
import { derivedIdempotencyKey, useIdempotencyIntent } from '@/services/idempotencyIntent';
import { reviewDesk } from '@/design-system';
import { ROUTES, reportRoute } from '@/navigation/routes';
import SampleReportViewer from '@/components/onboarding/SampleReportViewer';
import { useTips } from '@/context/TipsContext';
import { LibraryErrorBanner, LibraryErrorState } from '@/components/library/LibraryErrorState';
import { openFetchedUrlInNewTab } from '@/services/webDownload';
import { actionErrorMessage, chatSendErrorMessage } from '../chat/chatErrors';
import {
  filterLibraryItems,
  filterSharedItems,
  needsFullList,
  typeCount,
  type LibraryFilter,
} from './libraryDocuments';
import { useLibraryDocuments } from './useLibraryDocuments';
import { COLORS, isFailedTranscript } from './libraryFormat';
import { styles, webStyles } from './libraryStyles';
import { LibraryDeleteDialog, LibraryDocumentRow, LibrarySharedRow } from './components/LibraryRows';
import { LibraryIntro } from './components/LibraryIntro';
import { LibraryShareCenter } from './components/LibraryShareCenter';

type FilterType = LibraryFilter;

type CompletedTaskPayload = {
  document_id?: number;
  session_id?: number;
  analysis_task_id?: string;
  [key: string]: unknown;
};

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────

export default function LibraryScreen() {
  const { t, language } = useTranslation();
  const { showToast } = useToast();
  const audioMaxMb = useAudioUploadLimit();
  const router = useRouter();
  const { isPhone, isCompact } = useResponsive();
  const { autoAction } = useLocalSearchParams<{ autoAction?: string }>();
  const { showTip } = useTips();
  const [showSampleReport, setShowSampleReport] = useState(false);
  const { canShare, isB2BUser, isMentorOrAdmin, isSuper } = useTabAccess();
  const { billingStatus } = useAuth();
  // One rule with Chat (CHAT-22): the server's icf_progress entitlement.
  const canIncludeInIcfProgress = canTrackIcfProgress(billingStatus, {
    isSuperAdmin: isSuper,
    isSchoolMember: isB2BUser,
  });

  const { waitForTaskResult } = useTaskCompletion();

  const {
    items,
    setItems,
    sharedItems,
    sharedByMeItems,
    setSharedByMeItems,
    total,
    setTotal,
    loading,
    loadingMore,
    loadError,
    loadMoreError,
    sharedLoadError,
    hasMore,
    load,
    loadMore,
    ensureAllLoaded,
  } = useLibraryDocuments(isB2BUser);
  const [filter, setFilter] = useState<FilterType>('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Sharing state
  const [shareModalVisible, setShareModalVisible] = useState(false);
  const [shareDocumentId, setShareDocumentId] = useState<number | null>(null);
  const [shareDocumentName, setShareDocumentName] = useState('');

  // Notification state
  const [notificationModalVisible, setNotificationModalVisible] = useState(false);

  // Manage recipients state
  const [manageRecipientsVisible, setManageRecipientsVisible] = useState(false);
  const [manageDocumentId, setManageDocumentId] = useState<number | null>(null);
  const [manageDocumentName, setManageDocumentName] = useState('');

  // Upload state
  const [uploading, setUploading] = useState(false);

  // Viewer state
  const [viewerVisible, setViewerVisible] = useState(false);
  const [selectedTranscript, setSelectedTranscript] = useState<TranscriptContent | null>(null);
  const [selectedListItem, setSelectedListItem] = useState<TranscriptListItem | null>(null);
  const [loadingContent, setLoadingContent] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [deleteCandidate, setDeleteCandidate] = useState<UserDocumentItem | null>(null);
  const [deleteError, setDeleteError] = useState('');

  // Shared document viewer state
  const [viewingSharedDoc, setViewingSharedDoc] = useState<{
    name: string;
    date: string;
    note?: string;
  } | null>(null);

  // Analysis modal state
  const [analysisModalVisible, setAnalysisModalVisible] = useState(false);
  const [analyzeDocumentId, setAnalyzeDocumentId] = useState<number | null>(null);
  const [analyzeTranscriptName, setAnalyzeTranscriptName] = useState<string>('');
  const [analyses, setAnalyses] = useState<TranscriptAnalysisItem[]>([]);

  // Filtered and counted items. Search and type filters are client-side, so
  // they first pull in the remaining pages (CHAT-11).
  const filteredItems = filterLibraryItems(items, filter, searchQuery);
  const filteredSharedItems = filterSharedItems(sharedItems, searchQuery);
  const fullyLoaded = !hasMore;
  const transcriptCount = typeCount(items, 'transcripts', fullyLoaded);
  const documentCount = typeCount(items, 'documents', fullyLoaded);
  const materialCount = typeCount(items, 'materials', fullyLoaded);
  const sharedCount = sharedItems.length;

  useEffect(() => {
    if (hasMore && !loadMoreError && needsFullList(filter, searchQuery)) {
      void ensureAllLoaded();
    }
  }, [ensureAllLoaded, filter, hasMore, loadMoreError, searchQuery]);

  useEffect(() => {
    if (!loading && !loadError && transcriptCount === 0) {
      showTip('tip_voice_recording');
    }
  }, [loadError, loading, showTip, transcriptCount]);


  const handleView = async (item: UserDocumentItem) => {
    // For materials, download them
    if (item.document_type === 'user_material') {
      handleDownload(item);
      return;
    }

    // For text analysis documents, navigate to the associated chat session
    if (item.document_type === 'user_analysis') {
      if (item.session_id) {
        router.push({
          pathname: ROUTES.TABS_CHAT,
          params: { sessionId: String(item.session_id) },
        });
      } else {
        const msg = t('library.noSessionAvailable');
        showDialog(msg);
      }
      return;
    }

    if (item.transcription_status !== 'completed') {
      const msg =
        item.transcription_status === 'processing'
          ? t('library.transcriptProcessing')
          : t('library.transcriptUnavailable');
      showDialog(msg);
      return;
    }

    setLoadingContent(true);
    setSelectedListItem({
      id: item.id,
      original_filename: item.original_filename,
      audio_duration_seconds: item.audio_duration_seconds,
      transcript_language: item.transcript_language,
      transcription_status: item.transcription_status,
      grammar_correction_applied: item.grammar_correction_applied,
      created_at: item.created_at,
    } as TranscriptListItem);

    try {
      const content = await transcriptionAPI.getContent(item.id);
      setSelectedTranscript(content);
      setViewerVisible(true);
      loadAnalyses(item.id);
    } catch (e: any) {
      showDialog(actionErrorMessage(t, e, 'transcription.errors.loadFailed'));
    } finally {
      setLoadingContent(false);
    }
  };

  const handleDownload = async (item: UserDocumentItem) => {
    try {
      // Opens the tab inside the tap, then points it at the signed URL
      // (CHAT-13: window.open after an await is blocked on Safari).
      await openFetchedUrlInNewTab(async () => (await userDocumentsAPI.download(item.id)).download_url);
    } catch (e: any) {
      showDialog(actionErrorMessage(t, e, 'common.downloadFailed'));
    }
  };

  const performDelete = async (item: UserDocumentItem): Promise<boolean> => {
    const isAnalysis = item.document_type === 'user_analysis';
    const cascadeChat = isAnalysis;

    setDeleteError('');
    setDeletingId(item.id);

    try {
      await userDocumentsAPI.delete(item.id, cascadeChat);
      setItems((prev) => prev.filter((i) => i.id !== item.id));
      setSharedByMeItems((prev) => prev.filter((share) => share.document_id !== item.id));
      setTotal((prev) => Math.max(0, prev - 1));
      return true;
    } catch (e: any) {
      const msg = actionErrorMessage(t, e, 'library.deleteError');
      setDeleteError(msg);
      return false;
    } finally {
      setDeletingId(null);
    }
  };

  const handleDelete = (item: UserDocumentItem) => {
    setDeleteCandidate(item);
    setDeleteError('');
  };

  const handleConfirmWebDelete = async () => {
    if (!deleteCandidate) return;
    const deleted = await performDelete(deleteCandidate);
    if (deleted) {
      setDeleteCandidate(null);
    }
  };

  const closeViewer = () => {
    setViewerVisible(false);
    setSelectedTranscript(null);
    setSelectedListItem(null);
    setAnalyses([]);
    setViewingSharedDoc(null);
  };

  // Handle downloading a shared material
  const handleDownloadSharedMaterial = async (item: SharedDocument) => {
    try {
      const response = await documentSharingAPI.downloadMaterial(item.document_id);
      // Same-origin blob link, so `download` works without a user gesture.
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = item.original_filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (e: any) {
      showDialog(actionErrorMessage(t, e, 'common.downloadFailed'));
    }
  };

  // Handle viewing a shared document
  const handleViewShared = async (item: SharedDocument) => {
    // For materials, download directly
    if (item.document_type === 'user_material') {
      handleDownloadSharedMaterial(item);
      return;
    }

    // Only voice transcripts can be viewed for now
    if (item.document_type !== 'user_transcript') {
      const msg = t('library.textDocsNotViewable');
      showDialog(msg);
      return;
    }

    if (item.transcription_status !== 'completed') {
      const msg = item.transcription_status === 'processing'
        ? t('library.transcriptProcessing')
        : t('library.transcriptUnavailable');
      showDialog(msg);
      return;
    }

    setLoadingContent(true);
    setSelectedListItem({
      id: item.document_id,
      original_filename: item.original_filename,
      audio_duration_seconds: item.audio_duration_seconds,
      transcript_language: item.transcript_language,
      transcription_status: item.transcription_status,
      grammar_correction_applied: false,
      created_at: item.shared_at,
    } as TranscriptListItem);

    // Set shared by info for the banner
    setViewingSharedDoc({
      name: item.shared_by?.full_name || t('library.unknown'),
      date: item.shared_at,
      note: item.note,
    });

    try {
      const content = await transcriptionAPI.getContent(item.document_id);
      setSelectedTranscript(content);
      setViewerVisible(true);
      // Don't load analyses for shared documents - user doesn't own them
    } catch (e: any) {
      showDialog(actionErrorMessage(t, e, 'transcription.errors.loadFailed'));
      setViewingSharedDoc(null);
    } finally {
      setLoadingContent(false);
    }
  };

  const loadAnalyses = useCallback(async (documentId: number) => {
    try {
      const response = await transcriptionAPI.listAnalyses(documentId);
      setAnalyses(response.analyses || []);
    } catch (e) {
      console.error('Failed to load analyses:', e);
      setAnalyses([]);
    }
  }, []);

  const handleOpenAnalysisModal = useCallback(
    (documentId: number) => {
      const item = items.find((i) => i.id === documentId);
      setAnalyzeDocumentId(documentId);
      setAnalyzeTranscriptName(item?.original_filename || '');
      setAnalysisModalVisible(true);
    },
    [items]
  );

  // ASYNC-6: one Idempotency-Key per user intent. A double submit of the same
  // input reuses it (the backend replays the existing job); "Try again" after
  // a failure mints a new one, since the old key would return the failed job.
  const analysisIntent = useIdempotencyIntent();
  const transcriptionIntent = useIdempotencyIntent();
  /** Re-dispatches the analysis shown in the progress modal ("Try again"). */
  const analysisRetryRef = useRef<
    ((idempotencyKey: string) => Promise<{ task_id: string; session_id?: number | null }>) | null
  >(null);
  const retryInFlightRef = useRef(false);

  const handleAnalyzeSubmit = useCallback(
    async (options: AnalysisOptions) => {
      if (!analyzeDocumentId) return;

      // Close the config sheet and the source transcript viewer, then hand off
      // to the staged AnalysisProgress modal — same flow as Library audio/text
      // and chat transcript analysis.
      const sourceName = analyzeTranscriptName || `transcript_${analyzeDocumentId}`;
      const documentId = analyzeDocumentId;
      const dispatch = (idempotencyKey: string) =>
        transcriptionAPI.analyzeTranscript(documentId, { ...options, idempotencyKey });
      try {
        const response = await dispatch(analysisIntent.key(`transcript:${documentId}`));
        analysisRetryRef.current = dispatch;
        setAnalysisModalVisible(false);
        setAnalyzeDocumentId(null);
        setViewerVisible(false);
        setAnalysisDocumentName(sourceName);
        setTextAnalysisTaskId(response.task_id);
        if (response.session_id) {
          setTextAnalysisSessionId(response.session_id);
        }
        setShowTextAnalysisProgress(true);
      } catch (e: any) {
        showDialog(trialAnalysisUsedMessage(t, language, e) || chatSendErrorMessage(t, e, 'library.analysisFailed'));
        throw e;
      }
    },
    [analyzeDocumentId, analyzeTranscriptName, analysisIntent, language, t]
  );

  const handleViewAnalysis = useCallback(
    (sessionId: number) => {
      closeViewer();
      // Report lane (D0): "view analysis" opens the canonical report screen.
      router.push(reportRoute(sessionId) as never);
    },
    [router]
  );

  const handleOpenInChat = useCallback(
    (sessionId: number) => {
      closeViewer();
      router.push({
        pathname: ROUTES.TABS_CHAT,
        params: { sessionId: String(sessionId) },
      });
    },
    [router]
  );

  const closeAnalysisModal = useCallback(() => {
    analysisIntent.reset();
    setAnalysisModalVisible(false);
    setAnalyzeDocumentId(null);
    setAnalyzeTranscriptName('');
  }, [analysisIntent]);

  // Share handlers
  const handleOpenShareModal = useCallback((item: UserDocumentItem) => {
    setShareDocumentId(item.id);
    setShareDocumentName(item.original_filename);
    setShareModalVisible(true);
  }, []);

  const handleShareSuccess = useCallback(() => {
    setShareModalVisible(false);
    setShareDocumentId(null);
    setShareDocumentName('');
    // Refresh to update shared status
    load(true);
  }, [load]);

  const closeShareModal = useCallback(() => {
    setShareModalVisible(false);
    setShareDocumentId(null);
    setShareDocumentName('');
  }, []);

  // Manage share recipients handlers
  const handleOpenManageRecipients = useCallback((item: UserDocumentItem) => {
    setManageDocumentId(item.id);
    setManageDocumentName(item.original_filename);
    setManageRecipientsVisible(true);
  }, []);

  const closeManageRecipients = useCallback(() => {
    setManageRecipientsVisible(false);
    setManageDocumentId(null);
    setManageDocumentName('');
  }, []);

  // Upload material handler
  const handleUploadMaterial = useCallback(async () => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: [
          'application/pdf',
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          'text/plain',
        ],
        copyToCacheDirectory: true,
      });

      if (result.canceled || !result.assets || result.assets.length === 0) return;

      const file = result.assets[0];

      // Validate file size (50MB max)
      if (file.size && file.size > 50 * 1024 * 1024) {
        const msg = t('library.fileTooLarge');
        showDialog(msg);
        return;
      }

      setUploading(true);

      const formData = new FormData();
      // On web, file.file is a File object
      formData.append('file', (file as any).file || file);

      await documentSharingAPI.uploadMaterial(formData);

      showToast(t('library.uploadSuccess'), 'success');

      load(true); // Refresh the list
    } catch (e: any) {
      showDialog(actionErrorMessage(t, e, 'library.uploadFailed'));
    } finally {
      setUploading(false);
    }
  }, [load, t]);

  // ─── Text Document Upload Flow ───────────────────────────────────────────

  const [pendingTextFile, setPendingTextFile] = useState<{uri: string; name: string; mimeType: string; size?: number} | null>(null);
  const [showTextAnalysisModal, setShowTextAnalysisModal] = useState(false);
  const [textAnalysisTaskId, setTextAnalysisTaskId] = useState<string | null>(null);
  const [textAnalysisSessionId, setTextAnalysisSessionId] = useState<number | null>(null);
  const [showTextAnalysisProgress, setShowTextAnalysisProgress] = useState(false);
  const [analysisDocumentName, setAnalysisDocumentName] = useState<string | undefined>(undefined);
  const cancelledTaskIdsRef = useRef<Set<string>>(new Set());
  const cancelPendingTextAnalysisRef = useRef(false);
  const cancelPendingTranscriptionRef = useRef(false);

  // Library upload step analytics: extension and size bucket only, never the name.
  const trackPick = (outcome: LibraryPickOutcome, fileName?: string | null, size?: number | null) => {
    const picked = libraryPickEvent(outcome, fileName, size);
    trackAuthEvent(picked.event, picked.properties);
  };

  const handlePickTextDocument = useCallback(async () => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: [
          'application/pdf',
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          'text/plain',
        ],
        copyToCacheDirectory: true,
      });
      if (result.canceled || !result.assets?.length) return;
      const file = result.assets[0];
      if (file.size && file.size > 50 * 1024 * 1024) {
        trackPick('text_too_large', file.name, file.size);
        const msg = t('library.fileTooLarge');
        showDialog(msg);
        return;
      }
      trackPick('text', file.name, file.size);
      setPendingTextFile({
        uri: file.uri,
        name: file.name,
        mimeType: file.mimeType || 'application/octet-stream',
        size: file.size,
      });
      setShowTextAnalysisModal(true);
    } catch (e: any) {
      showDialog(actionErrorMessage(t, e, 'library.uploadFailed'));
    }
  }, [t]);

  const handleTextAnalysisSubmit = useCallback(async (options: AnalysisOptions) => {
    if (!pendingTextFile) return;
    try {
      setShowTextAnalysisModal(false);
      setAnalysisDocumentName(pendingTextFile.name);
      setShowTextAnalysisProgress(true);
      cancelPendingTextAnalysisRef.current = false;
      const fileData = {
        uri: pendingTextFile.uri,
        type: pendingTextFile.mimeType,
        name: pendingTextFile.name,
      };
      const dispatch = (idempotencyKey: string) =>
        documentsAPI.analyze(fileData, { ...options, idempotencyKey });
      const response = await dispatch(analysisIntent.key(pendingTextFile));
      analysisRetryRef.current = dispatch;
      if (cancelPendingTextAnalysisRef.current) {
        cancelledTaskIdsRef.current.add(response.task_id);
        try {
          await taskAPI.cancel(response.task_id);
        } catch (cancelError) {
          console.warn('Failed to cancel just-created text analysis task:', cancelError);
        }
        setShowTextAnalysisProgress(false);
        setAnalysisDocumentName(undefined);
        return;
      }
      setTextAnalysisTaskId(response.task_id);
      if (response.session_id) {
        setTextAnalysisSessionId(response.session_id);
      }
      trackAuthEvent('document_analysis_started', { surface: 'library', input: 'text' });
    } catch (e: any) {
      trackAuthEvent('document_analysis_failed', { surface: 'library', input: 'text', error_code: analyticsErrorCode(e) });
      setShowTextAnalysisProgress(false);
      setAnalysisDocumentName(undefined);
      showDialog(trialAnalysisUsedMessage(t, language, e) || chatSendErrorMessage(t, e, 'library.analysisFailed'));
    } finally {
      setPendingTextFile(null);
    }
  }, [pendingTextFile, analysisIntent, language, t]);

  const handleTextAnalysisComplete = useCallback((sessionId: number) => {
    analysisIntent.reset();
    analysisRetryRef.current = null;
    setTextAnalysisSessionId(sessionId);
    // Auto-navigate to chat after a brief moment to show the "complete" state.
    // The chat screen auto-opens the analysis side panel for sessions with analyses.
    setTimeout(() => {
      setShowTextAnalysisProgress(false);
      setTextAnalysisTaskId(null);
      setTextAnalysisSessionId(null);
      setAnalysisDocumentName(undefined);
      load(true);
      router.push({
        pathname: ROUTES.TABS_CHAT,
        params: { sessionId: String(sessionId) },
      });
    }, 1200);
  }, [analysisIntent, load, router]);

  // Terminal failure: the next submit of this input is a new intent.
  const handleTextAnalysisError = useCallback(() => {
    analysisIntent.reset();
  }, [analysisIntent]);

  // "Try again" on a failed analysis: same input, NEW Idempotency-Key (the old
  // key would replay the failed job).
  const handleTextAnalysisRetry = useCallback(async () => {
    const dispatch = analysisRetryRef.current;
    if (retryInFlightRef.current) return;
    if (!dispatch) {
      // Input no longer available: back to the library to start over.
      analysisIntent.reset();
      setShowTextAnalysisProgress(false);
      setAnalysisDocumentName(undefined);
      return;
    }
    retryInFlightRef.current = true;
    cancelPendingTextAnalysisRef.current = false;
    setTextAnalysisTaskId(null);
    setTextAnalysisSessionId(null);
    try {
      const response = await dispatch(analysisIntent.renew());
      if (cancelPendingTextAnalysisRef.current) {
        cancelledTaskIdsRef.current.add(response.task_id);
        try {
          await taskAPI.cancel(response.task_id);
        } catch (cancelError) {
          console.warn('Failed to cancel just-created retry analysis task:', cancelError);
        }
        return;
      }
      setTextAnalysisTaskId(response.task_id);
      if (response.session_id) {
        setTextAnalysisSessionId(response.session_id);
      }
    } catch (e: any) {
      setShowTextAnalysisProgress(false);
      setAnalysisDocumentName(undefined);
      const msg = trialAnalysisUsedMessage(t, language, e) || noCreditMessage(t, e) || userFacingErrorMessage(t, e?.message || e);
      showDialog(msg);
    } finally {
      retryInFlightRef.current = false;
    }
  }, [analysisIntent, language, t]);

  const handleViewTextAnalysis = useCallback((sessionId: number) => {
    setShowTextAnalysisProgress(false);
    setTextAnalysisTaskId(null);
    setTextAnalysisSessionId(null);
    setAnalysisDocumentName(undefined);
    load(true);
    router.push({
      pathname: ROUTES.TABS_CHAT,
      params: { sessionId: String(sessionId) },
    });
  }, [load, router]);

  const handleTextAnalysisCancel = useCallback((taskId?: string | null) => {
    if (taskId) {
      cancelledTaskIdsRef.current.add(taskId);
    } else {
      cancelPendingTextAnalysisRef.current = true;
    }
    analysisIntent.reset();
    analysisRetryRef.current = null;
    setShowTextAnalysisProgress(false);
    setTextAnalysisTaskId(null);
    setTextAnalysisSessionId(null);
    setAnalysisDocumentName(undefined);
    load(true);
  }, [analysisIntent, load]);

  const handleTextAnalysisProgressClose = useCallback(() => {
    analysisIntent.reset();
    analysisRetryRef.current = null;
    setShowTextAnalysisProgress(false);
    setAnalysisDocumentName(undefined);
    load(true);
  }, [analysisIntent, load]);

  // ─── Voice Recording Upload Flow ──────────────────────────────────────────

  const [pendingAudioFile, setPendingAudioFile] = useState<{uri: string; name: string; type: string; size?: number} | null>(null);
  const [showTranscriptionModal, setShowTranscriptionModal] = useState(false);
  const [showTranscriptionProgress, setShowTranscriptionProgress] = useState(false);
  const [transcriptionTaskId, setTranscriptionTaskId] = useState<string | null>(null);
  const [, setTranscriptionDocId] = useState<number | null>(null);
  const [transcriptionEstimate, setTranscriptionEstimate] = useState<{
    seconds?: number | null;
    minSeconds?: number | null;
    maxSeconds?: number | null;
  } | null>(null);

  const isTaskCancellationError = useCallback((error: any, ...taskIds: (string | null | undefined)[]) => {
    const message = String(error?.message || error || '').toLowerCase();
    return message.includes('cancel') || taskIds.some((id) => Boolean(id && cancelledTaskIdsRef.current.has(id)));
  }, []);

  const handleTranscriptionProgressCancel = useCallback((taskId?: string | null) => {
    if (taskId) {
      cancelledTaskIdsRef.current.add(taskId);
    } else {
      cancelPendingTranscriptionRef.current = true;
    }
    setShowTranscriptionProgress(false);
    setTranscriptionTaskId(null);
    setTranscriptionDocId(null);
    setTranscriptionEstimate(null);
    setPendingAudioFile(null);
    load(true);
  }, [load]);

  const handlePickVoiceRecording = useCallback(async () => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: AUDIO_UPLOAD_MIME_TYPES,
        copyToCacheDirectory: true,
      });
      if (result.canceled || !result.assets?.length) return;
      const file = result.assets[0];
      if (file.size && file.size > audioMaxBytes(audioMaxMb)) {
        trackPick('audio_too_large', file.name, file.size);
        const msg = t('transcription.errors.audioTooLarge', { limit: audioMaxMb });
        showDialog(msg);
        return;
      }
      trackPick('audio', file.name, file.size);
      setPendingAudioFile({
        uri: file.uri,
        name: file.name,
        type: file.mimeType || 'audio/mpeg',
        size: file.size,
      });
      setShowTranscriptionModal(true);
    } catch (e: any) {
      const msg = userFacingErrorMessage(t, e?.message || e);
      showDialog(msg);
    }
  }, [t, audioMaxMb]);

  // Unified session picker: accepts both audio and text/transcript files,
  // then routes to the correct flow (transcription vs. text analysis) based
  // on the picked file's MIME type (with an extension-based fallback).
  const handlePickSessionFile = useCallback(async () => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: [
          ...AUDIO_UPLOAD_MIME_TYPES,
          'application/pdf',
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          'text/plain',
        ],
        copyToCacheDirectory: true,
      });
      if (result.canceled || !result.assets?.length) return;
      const file = result.assets[0];

      const mime = (file.mimeType || '').toLowerCase();
      const name = (file.name || '').toLowerCase();
      const ext = name.includes('.') ? name.slice(name.lastIndexOf('.') + 1) : '';
      // Unknown audio/* types still go to the server, which answers with a
      // specific "unsupported format" message (JOB-R22).
      const isAudio = isAudioUploadFile(name, mime) || mime.startsWith('audio/');
      const isText =
        mime === 'application/pdf' ||
        mime === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
        mime === 'text/plain' ||
        ['pdf', 'docx', 'txt'].includes(ext);

      if (!isAudio && !isText) {
        trackPick('unsupported', file.name, file.size);
        const msg = t(
          'library.unsupportedFileType'
        );
        showDialog(msg);
        return;
      }

      if (isAudio) {
        if (file.size && file.size > audioMaxBytes(audioMaxMb)) {
          trackPick('audio_too_large', file.name, file.size);
          const msg = t('transcription.errors.audioTooLarge', { limit: audioMaxMb });
          showDialog(msg);
          return;
        }
        trackPick('audio', file.name, file.size);
        setPendingAudioFile({
          uri: file.uri,
          name: file.name,
          type: file.mimeType || 'audio/mpeg',
          size: file.size,
        });
        setShowTranscriptionModal(true);
        return;
      }

      if (file.size && file.size > 50 * 1024 * 1024) {
        trackPick('text_too_large', file.name, file.size);
        const msg = t('library.fileTooLarge');
        showDialog(msg);
        return;
      }
      trackPick('text', file.name, file.size);
      setPendingTextFile({
        uri: file.uri,
        name: file.name,
        mimeType: file.mimeType || 'application/octet-stream',
        size: file.size,
      });
      setShowTextAnalysisModal(true);
    } catch (e: any) {
      const msg = userFacingErrorMessage(t, e?.message || e);
      showDialog(msg);
    }
  }, [t, audioMaxMb]);

  // Dashboard "Upload" deep link (autoAction=pickSession|pickAudio|pickText).
  // Browsers only open a file dialog from a user gesture, so opening it from
  // a timer did nothing on Safari (and Chrome). Highlight the upload panel
  // instead and clear the param so it doesn't fire again (CHAT-24).
  const [uploadHighlighted, setUploadHighlighted] = useState(false);
  useEffect(() => {
    if (!autoAction) return;
    setUploadHighlighted(true);
    router.setParams({ autoAction: undefined } as any);
  }, [autoAction, router]);

  const handleStartTranscription = useCallback(async (options: TranscriptionOptions) => {
    if (!pendingAudioFile) return;
    let activeTranscriptionTaskId: string | null = null;
    let activeAnalysisTaskId: string | null = null;
    cancelPendingTranscriptionRef.current = false;

    setShowTranscriptionModal(false);
    setShowTranscriptionProgress(true);
    // Same picked file => same key, so a double "Start" replays one upload.
    const transcriptionKey = transcriptionIntent.key(pendingAudioFile);

    try {
      const response = await transcriptionAPI.transcribe(
        { uri: pendingAudioFile.uri, type: pendingAudioFile.type, name: pendingAudioFile.name },
        { ...options, uiLanguage: language, idempotencyKey: transcriptionKey },
      );
      activeTranscriptionTaskId = response.task_id;
      if (cancelPendingTranscriptionRef.current) {
        cancelledTaskIdsRef.current.add(response.task_id);
        try {
          await taskAPI.cancel(response.task_id);
        } catch (cancelError) {
          console.warn('Failed to cancel just-created transcription task:', cancelError);
        }
        setShowTranscriptionProgress(false);
        setTranscriptionTaskId(null);
        setTranscriptionDocId(null);
        setTranscriptionEstimate(null);
        setPendingAudioFile(null);
        return;
      }
      setTranscriptionTaskId(response.task_id);
      setTranscriptionDocId(response.document_id);
      setTranscriptionEstimate({
        seconds: response.estimated_completion_seconds,
        minSeconds: response.estimated_completion_min_seconds,
        maxSeconds: response.estimated_completion_max_seconds,
      });

      // Wait for task completion (WebSocket + polling fallback)
      const transcriptionResult = await waitForTaskResult<CompletedTaskPayload>(response.task_id);

      setTranscriptionTaskId(null);

      const audioName = pendingAudioFile?.name || 'Recording';

      // Auto-chain into analysis. Backend may already have started the analysis
      // (transcribe accepts analysisInstructions) — prefer its task_id when present.
      const backendAnalysisTaskId: string | undefined = transcriptionResult?.analysis_task_id;
      const backendAnalysisSessionId: number | undefined = transcriptionResult?.session_id;

      // Hand off from transcription modal to analysis modal in the same instant
      // (no transcript-viewer review step — user picked auto-analyze flow).
      setShowTranscriptionProgress(false);
      setTranscriptionDocId(null);
      setTranscriptionEstimate(null);
      setAnalysisDocumentName(audioName);
      cancelPendingTextAnalysisRef.current = false;
      setShowTextAnalysisProgress(true);

      // Default ICF / auto options for the chained analysis (and its retry).
      const defaultOptions: AnalysisOptions = {
        analysisMode: 'icf',
        requestedLevel: 'auto',
        includeMarkerTable: false,
        includeInIcfProgress: canIncludeInIcfProgress,
        language: language === 'en' ? 'en' : 'tr',
      };

      try {
        if (backendAnalysisTaskId && backendAnalysisSessionId) {
          activeAnalysisTaskId = backendAnalysisTaskId;
          // "Try again" re-analyzes the existing transcript with the options
          // the backend used (no second audio upload).
          const retryOptions: AnalysisOptions = {
            ...defaultOptions,
            includeInIcfProgress: canIncludeInIcfProgress && options.includeInICFProgress !== false,
            analysisInstructions: options.analysisInstructions,
          };
          analysisRetryRef.current = (idempotencyKey: string) =>
            transcriptionAPI.analyzeTranscript(response.document_id, { ...retryOptions, idempotencyKey });
          if (cancelPendingTextAnalysisRef.current) {
            cancelledTaskIdsRef.current.add(backendAnalysisTaskId);
            try {
              await taskAPI.cancel(backendAnalysisTaskId);
            } catch (cancelError) {
              console.warn('Failed to cancel just-created analysis task:', cancelError);
            }
            setShowTextAnalysisProgress(false);
            setAnalysisDocumentName(undefined);
            return;
          }
          // Backend auto-started analysis during transcription — just observe it.
          setTextAnalysisTaskId(backendAnalysisTaskId);
          setTextAnalysisSessionId(backendAnalysisSessionId);
        } else {
          // Kick off analysis with default ICF / auto options. Its key derives
          // from the upload's, so a replayed upload never starts a 2nd analysis.
          const dispatch = (idempotencyKey: string) =>
            transcriptionAPI.analyzeTranscript(response.document_id, { ...defaultOptions, idempotencyKey });
          const analysisResponse = await dispatch(derivedIdempotencyKey(transcriptionKey, 'analysis'));
          analysisRetryRef.current = dispatch;
          activeAnalysisTaskId = analysisResponse.task_id;
          if (cancelPendingTextAnalysisRef.current) {
            cancelledTaskIdsRef.current.add(analysisResponse.task_id);
            try {
              await taskAPI.cancel(analysisResponse.task_id);
            } catch (cancelError) {
              console.warn('Failed to cancel just-created analysis task:', cancelError);
            }
            setShowTextAnalysisProgress(false);
            setAnalysisDocumentName(undefined);
            return;
          }
          setTextAnalysisTaskId(analysisResponse.task_id);
          if (analysisResponse.session_id) {
            setTextAnalysisSessionId(analysisResponse.session_id);
          }
        }
        load(true);
      } catch (analysisErr: any) {
        // Analysis kick-off failed — fall back to opening the transcript viewer
        // so the user can manually retry analysis.
        console.warn('Auto-analysis failed, falling back to transcript viewer:', analysisErr);
        setShowTextAnalysisProgress(false);
        setAnalysisDocumentName(undefined);
        // A spent card-backed trial analysis or a missing credit is not
        // retryable: say why.
        const blocked = trialAnalysisUsedMessage(t, language, analysisErr) || noCreditMessage(t, analysisErr);
        if (blocked) showDialog(blocked);
        const transcript = await transcriptionAPI.getContent(response.document_id);
        setSelectedTranscript(transcript);
        setSelectedListItem({
          id: response.document_id,
          original_filename: audioName,
          audio_duration_seconds: transcript.duration_seconds,
          transcript_language: transcript.language,
          transcription_status: 'completed',
          grammar_correction_applied: transcript.grammar_corrected,
          created_at: new Date().toISOString(),
        } as TranscriptListItem);
        setViewerVisible(true);
        load(true);
      }
    } catch (e: any) {
      if (isTaskCancellationError(e, activeTranscriptionTaskId, activeAnalysisTaskId)) {
        setTranscriptionTaskId(null);
        setTranscriptionDocId(null);
        setTranscriptionEstimate(null);
        setShowTranscriptionProgress(false);
        setShowTextAnalysisProgress(false);
        setTextAnalysisTaskId(null);
        setTextAnalysisSessionId(null);
        setAnalysisDocumentName(undefined);
        setPendingAudioFile(null);
        return;
      }

      setTranscriptionTaskId(null);
      setTranscriptionDocId(null);
      setTranscriptionEstimate(null);
      const failure = transcriptionFailureEvent(e, 'library');
      trackAuthEvent(failure.event, failure.properties);
      setTimeout(() => {
        setShowTranscriptionProgress(false);
        const msg = trialAnalysisUsedMessage(t, language, e)
          || noCreditMessage(t, e)
          || voiceMinutesExceededMessage(t, e)
          || audioUploadRejectionMessage(t, e)
          || userFacingErrorMessage(t, e?.message || e);
        showDialog(msg);
      }, 2000);
    } finally {
      // Upload intent over (terminal, cancelled or handed off to analysis).
      transcriptionIntent.reset();
      setPendingAudioFile(null);
    }
  }, [pendingAudioFile, language, t, waitForTaskResult, load, isTaskCancellationError, transcriptionIntent]);

  const isCompactWeb = isCompact;
  const visibleItems = filter === 'shared' ? [] : filteredItems;
  const visibleSharedItems = filter === 'shared' ? filteredSharedItems : [];
  const totalVisible = filter === 'shared' ? visibleSharedItems.length : visibleItems.length;
  const sharedOutCount = sharedByMeItems.length;
  const latestSharedOut = sharedByMeItems[0];
  const shellRecentItems = items.filter((item) => !isFailedTranscript(item)).slice(0, 4).map((item) => ({
    id: item.id,
    title: item.original_filename,
    subtitle: item.document_type === 'user_transcript'
      ? t('library.typeVoice')
      : t('library.typeText'),
    onPress: () => handleView(item),
  }));
  const filterOptions = [
    { key: 'all' as FilterType, label: t('library.filterAll'), count: total },
    { key: 'transcripts' as FilterType, label: t('library.filterTranscripts'), count: transcriptCount },
    { key: 'documents' as FilterType, label: t('library.filterDocuments'), count: documentCount },
    ...(isMentorOrAdmin
      ? [{ key: 'materials' as FilterType, label: t('library.filterMaterials'), count: materialCount }]
      : []),
    ...(isB2BUser
      ? [{ key: 'shared' as FilterType, label: t('library.filterShared'), count: sharedCount }]
      : []),
  ];

  const openLatestSharedOut = () => {
    if (!latestSharedOut) return;
    setManageDocumentId(latestSharedOut.document_id);
    setManageDocumentName(latestSharedOut.original_filename);
    setManageRecipientsVisible(true);
  };

  return (
    <>
      <WebAppShell
        active="sessions"
        recentItems={shellRecentItems}
        contentMaxWidth="100%"
        topRight={
          <>
            {isB2BUser && <NotificationBell onPress={() => setNotificationModalVisible(true)} />}
            <TouchableOpacity style={webStyles.topIconButton} onPress={() => load(true)} accessibilityRole="button">
              <MaterialIcons name="refresh" size={19} color={reviewDesk.inkSoft} />
            </TouchableOpacity>
          </>
        }
      >
        <LibraryIntro
          isPhone={isPhone}
          isCompact={isCompactWeb}
          highlighted={uploadHighlighted}
          uploading={uploading}
          onPickSession={handlePickSessionFile}
          onPickVoice={handlePickVoiceRecording}
          onPickText={handlePickTextDocument}
        />

        {isB2BUser && (
          <LibraryShareCenter
            isCompact={isCompactWeb}
            isMentorOrAdmin={isMentorOrAdmin}
            canShare={canShare}
            sharedWithMeCount={sharedCount}
            sharedByMeCount={sharedOutCount}
            latestSharedOut={latestSharedOut}
            onOpenInbox={() => setFilter('shared')}
            onManageLatest={openLatestSharedOut}
          />
        )}

        <View style={[webStyles.contentGrid, isCompactWeb && webStyles.contentGridCompact]}>
          <View style={webStyles.sessionsPanel}>
            <View style={[webStyles.listToolbar, isCompactWeb && webStyles.listToolbarCompact]}>
              <View style={webStyles.searchBox}>
                <MaterialIcons name="search" size={22} color={reviewDesk.muted} />
                <TextInput
                  value={searchQuery}
                  onChangeText={setSearchQuery}
                  placeholder={t('library.webSearchPlaceholder')}
                  placeholderTextColor={reviewDesk.muted}
                  style={webStyles.searchInput}
                />
              </View>
              <View style={[webStyles.webFilters, isCompactWeb && webStyles.webFiltersCompact]}>
                {filterOptions.map((option) => {
                  const activeFilter = filter === option.key;
                  return (
                    <TouchableOpacity
                      key={option.key}
                      onPress={() => setFilter(option.key)}
                      style={[webStyles.webFilterChip, activeFilter && webStyles.webFilterChipActive]}
                      accessibilityRole="button"
                      accessibilityState={{ selected: activeFilter }}
                    >
                      <Text className={activeFilter ? 'text-white' : undefined} style={[webStyles.webFilterText, activeFilter && webStyles.webFilterTextActive]}>
                        {option.label}
                      </Text>
                      {option.count != null && (
                        <Text className={activeFilter ? 'text-white' : undefined} style={[webStyles.webFilterCount, activeFilter && webStyles.webFilterTextActive]}>
                          {option.count}
                        </Text>
                      )}
                    </TouchableOpacity>
                  );
                })}
              </View>
            </View>

            {!loading && !!loadError && items.length > 0 && filter !== 'shared' && (
              <LibraryErrorBanner
                message={t('chatLibrary.library.refreshError')}
                retryLabel={t('common.retry')}
                onRetry={() => load(true)}
                testID="library_refresh_error"
              />
            )}
            {loading || (loadingMore && totalVisible === 0 && needsFullList(filter, searchQuery)) ? (
              <View style={webStyles.webLoading}>
                <ActivityIndicator size="large" color={reviewDesk.accent} />
                <Text style={webStyles.webLoadingText}>{t('common.loading')}</Text>
              </View>
            ) : filter !== 'shared' && !!loadError && items.length === 0 ? (
              // CHAT-10: a failed load is an error with retry, not an empty library.
              <LibraryErrorState
                title={t('chatLibrary.library.loadErrorTitle')}
                body={t('chatLibrary.library.loadErrorBody')}
                retryLabel={t('common.retry')}
                onRetry={() => load()}
                testID="library_load_error"
              />
            ) : filter === 'shared' && !!sharedLoadError ? (
              <LibraryErrorState
                title={t('chatLibrary.library.sharedLoadError')}
                body={t('chatLibrary.library.loadErrorBody')}
                retryLabel={t('common.retry')}
                onRetry={() => load(true)}
                testID="library_shared_load_error"
              />
            ) : totalVisible === 0 ? (
              <View style={webStyles.webEmpty}>
                <View style={webStyles.webEmptyIcon}>
                  <MaterialIcons name="folder-open" size={38} color={reviewDesk.accent} />
                </View>
                <Text style={webStyles.webEmptyTitle}>{t('library.webEmptyTitle')}</Text>
                <Text style={webStyles.webEmptyText}>
                  {t('library.webEmptyBody')}
                </Text>
                <TouchableOpacity style={webStyles.secondaryButton} onPress={handlePickSessionFile}>
                  <MaterialIcons name="cloud-upload" size={18} color={reviewDesk.accent} />
                  <Text style={webStyles.secondaryButtonText}>{t('library.webEmptyCta')}</Text>
                </TouchableOpacity>
              </View>
            ) : (
              <View style={webStyles.webList}>
                {filter === 'shared'
                  ? visibleSharedItems.map((item) => (
                    <LibrarySharedRow
                      key={`shared-${item.share_id}`}
                      item={item}
                      isPhone={isPhone}
                      onOpen={handleViewShared}
                    />
                  ))
                  : visibleItems.map((item) => (
                    <LibraryDocumentRow
                      key={item.id}
                      item={item}
                      isPhone={isPhone}
                      canShare={canShare}
                      deleting={deletingId === item.id}
                      onOpen={handleView}
                      onDelete={handleDelete}
                      onShare={handleOpenShareModal}
                      onManageSharing={handleOpenManageRecipients}
                    />
                  ))}
              </View>
            )}
            {!loading && filter !== 'shared' && !!loadMoreError && (
              <LibraryErrorBanner
                message={t('chatLibrary.library.loadMoreError')}
                retryLabel={t('common.retry')}
                onRetry={() => { void loadMore(); }}
                testID="library_load_more_error"
              />
            )}
            {!loading && filter !== 'shared' && hasMore && !loadMoreError && items.length > 0 && (
              // CHAT-11: the API returns 100 at a time.
              <TouchableOpacity
                style={webStyles.loadMoreButton}
                onPress={() => { void loadMore(); }}
                disabled={loadingMore}
                accessibilityRole="button"
                testID="library_load_more"
              >
                {loadingMore ? (
                  <ActivityIndicator size="small" color={reviewDesk.accent} />
                ) : (
                  <Text style={webStyles.loadMoreText}>
                    {t('chatLibrary.library.loadMore', { shown: items.length, total })}
                  </Text>
                )}
              </TouchableOpacity>
            )}
          </View>

          <View style={[webStyles.sidePanel, isCompactWeb && webStyles.sidePanelCompact]}>
            <View style={webStyles.sideIllustration}>
              <MaterialIcons name="description" size={54} color={reviewDesk.accent} />
              <View style={webStyles.sideArrow}>
                <MaterialIcons name="arrow-upward" size={26} color="#FFFFFF" />
              </View>
            </View>
            <Text style={webStyles.sideTitle}>
              {total === 0 ? t('library.webSideTitleFirst') : t('library.webSideTitleNext')}
            </Text>
            <Text style={webStyles.sideText}>
              {t('library.webSideBody')}
            </Text>
            <TouchableOpacity style={webStyles.primaryButtonWide} onPress={handlePickSessionFile}>
              <MaterialIcons name="cloud-upload" size={20} color="#FFFFFF" />
              <Text className="text-white" style={webStyles.primaryButtonText}>{t('library.webSideUpload')}</Text>
            </TouchableOpacity>
            <TouchableOpacity style={webStyles.linkButton} onPress={handlePickTextDocument}>
              <Text style={webStyles.linkButtonText}>{t('library.webSideTranscript')}</Text>
              <MaterialIcons name="chevron-right" size={18} color={reviewDesk.accent} />
            </TouchableOpacity>
            {total === 0 ? (
              <TouchableOpacity style={webStyles.linkButton} onPress={() => setShowSampleReport(true)}>
                <Text style={webStyles.linkButtonText}>{t('library.sampleTranscriptOpen')}</Text>
                <MaterialIcons name="visibility" size={18} color={reviewDesk.accent} />
              </TouchableOpacity>
            ) : null}
          </View>
        </View>

        <View style={[webStyles.privacyBanner, isPhone && webStyles.privacyBannerPhone]}>
          <MaterialIcons name="lock-outline" size={20} color={reviewDesk.accent} />
          <View style={{ flex: 1 }}>
            <Text style={webStyles.privacyTitle}>{t('library.webPrivacyTitle')}</Text>
            <Text style={webStyles.privacyText}>
              {t('library.webPrivacyBody')}
            </Text>
          </View>
        </View>
      </WebAppShell>

      <AnalysisModal
        visible={showTextAnalysisModal}
        onClose={() => { setShowTextAnalysisModal(false); setPendingTextFile(null); }}
        onSubmit={handleTextAnalysisSubmit}
        transcriptName={pendingTextFile?.name}
      />
      <AnalysisProgress
        visible={showTextAnalysisProgress}
        taskId={textAnalysisTaskId}
        sessionId={textAnalysisSessionId}
        documentName={analysisDocumentName}
        onComplete={handleTextAnalysisComplete}
        onError={handleTextAnalysisError}
        onRetry={handleTextAnalysisRetry}
        onViewAnalysis={handleViewTextAnalysis}
        onClose={handleTextAnalysisProgressClose}
        onCancel={handleTextAnalysisCancel}
      />
      <TranscriptionModal
        visible={showTranscriptionModal}
        onClose={() => { setShowTranscriptionModal(false); setPendingAudioFile(null); }}
        onStart={handleStartTranscription}
        file={pendingAudioFile}
      />
      <TranscriptionProgress
        visible={showTranscriptionProgress}
        status={null}
        taskId={transcriptionTaskId}
        estimatedSeconds={transcriptionEstimate?.seconds}
        estimatedMinSeconds={transcriptionEstimate?.minSeconds}
        estimatedMaxSeconds={transcriptionEstimate?.maxSeconds}
        onCancel={handleTranscriptionProgressCancel}
      />
      {loadingContent && (
        <View style={styles.loadingOverlay}>
          <View style={styles.loadingCard}>
            <ActivityIndicator size="large" color={COLORS.primary} />
            <Text style={styles.loadingOverlayText}>{t('library.loadingDocument')}</Text>
          </View>
        </View>
      )}
      <TranscriptViewer
        visible={viewerVisible}
        onClose={closeViewer}
        transcript={selectedTranscript}
        listItem={selectedListItem || undefined}
        onRefreshFeedback={handleOpenAnalysisModal}
        analyses={analyses}
        onViewAnalysis={handleViewAnalysis}
        onOpenInChat={handleOpenInChat}
        onShareWithMentor={(documentId) => {
          const docName = selectedListItem?.original_filename || (selectedTranscript as any)?.original_filename || 'transcript';
          setShareDocumentId(documentId);
          setShareDocumentName(docName);
          setShareModalVisible(true);
        }}
        onRefreshAnalyses={selectedTranscript?.id ? () => loadAnalyses(selectedTranscript.id) : undefined}
        sharedBy={viewingSharedDoc || undefined}
        isSharedDocument={viewingSharedDoc !== null}
        onTranscriptUpdated={setSelectedTranscript}
      />
      <AnalysisModal
        visible={analysisModalVisible}
        onClose={closeAnalysisModal}
        onSubmit={handleAnalyzeSubmit}
        transcriptName={analyzeTranscriptName}
      />
      {shareDocumentId && (
        <ShareDocumentModal
          visible={shareModalVisible}
          onClose={closeShareModal}
          documentId={shareDocumentId}
          documentName={shareDocumentName}
          onShareSuccess={handleShareSuccess}
        />
      )}
      {manageDocumentId && (
        <ManageShareRecipientsModal
          visible={manageRecipientsVisible}
          onClose={closeManageRecipients}
          documentId={manageDocumentId}
          documentName={manageDocumentName}
          onRecipientsChanged={() => load(true)}
        />
      )}
      <NotificationListModal
        visible={notificationModalVisible}
        onClose={() => setNotificationModalVisible(false)}
      />
      <SampleReportViewer
        visible={showSampleReport}
        surface="library_empty_state"
        primaryAction="upload"
        onClose={() => setShowSampleReport(false)}
        onPrimaryPress={() => {
          setShowSampleReport(false);
          handlePickSessionFile();
        }}
      />
      {deleteCandidate && (
        <LibraryDeleteDialog
          item={deleteCandidate}
          deleting={deletingId === deleteCandidate.id}
          error={deleteError}
          onCancel={() => setDeleteCandidate(null)}
          onConfirm={handleConfirmWebDelete}
        />
      )}
    </>
  );
}
